/**
 * Single source of truth for per-notification-type icons + colors.
 *
 * Both the bell dropdown (components/NotificationPanel.tsx) and the full
 * /notifications page (pages/Notifications.tsx) import from here so a given
 * notification type always renders the SAME icon in both places. Every type the
 * backend emits (see create_notification callers + lib/notificationCatalog.ts)
 * should have an entry — anything missing falls back to a generic dot, which is
 * the bug this module exists to prevent.
 */
import {
  Info,
  Share2,
  Globe,
  UserPlus,
  UserCheck,
  XCircle,
  ArrowRightLeft,
  Hammer,
  CheckCircle2,
  CheckCheck,
  AlertTriangle,
  StopCircle,
  HardDrive,
  HelpCircle,
  MessageSquare,
  Megaphone,
  Shield,
  type LucideIcon,
} from 'lucide-react'

export interface NotificationTypeConfig {
  icon: LucideIcon
  color: string
  /** Admin-only notification type (used by the /notifications filter UI). */
  isAdmin?: boolean
}

export const NOTIFICATION_TYPE_CONFIG: Record<string, NotificationTypeConfig> = {
  // Sharing + access
  app_shared: { icon: Share2, color: 'text-blue-500' },
  access_request: { icon: UserPlus, color: 'text-purple-500' },
  access_approved: { icon: UserCheck, color: 'text-green-500' },
  access_denied: { icon: XCircle, color: 'text-red-500' },

  // Ownership transfer
  app_transfer_request: { icon: ArrowRightLeft, color: 'text-amber-500' },
  app_transfer_accepted: { icon: ArrowRightLeft, color: 'text-green-500' },
  app_transfer_declined: { icon: ArrowRightLeft, color: 'text-red-500' },
  app_transfer_cancelled: { icon: ArrowRightLeft, color: 'text-muted-foreground' },

  // Publishing / builds
  production_build_triggered: { icon: Hammer, color: 'text-amber-500' },
  production_build_success: { icon: CheckCircle2, color: 'text-green-500' },
  production_build_failed: { icon: XCircle, color: 'text-red-500' },
  new_public_app: { icon: Globe, color: 'text-purple-500' },

  // Public access lifecycle
  public_access_approved: { icon: Globe, color: 'text-green-500' },
  public_access_rejected: { icon: Globe, color: 'text-red-500' },

  // App runtime / storage
  app_error: { icon: AlertTriangle, color: 'text-red-500' },
  app_auto_stopped: { icon: StopCircle, color: 'text-muted-foreground' },
  app_storage_quota_warning: { icon: HardDrive, color: 'text-amber-500' },
  app_storage_quota_exceeded: { icon: HardDrive, color: 'text-red-500' },
  app_storage_over_quota_growth: { icon: HardDrive, color: 'text-red-500' },

  // Claude workspace
  claude_awaiting_input: { icon: HelpCircle, color: 'text-amber-500' },
  claude_task_complete: { icon: CheckCheck, color: 'text-green-500' },

  // Platform
  changelog_released: { icon: Megaphone, color: 'text-primary' },
  survey_invite: { icon: MessageSquare, color: 'text-primary' },

  // Feedback
  feedback_comment: { icon: MessageSquare, color: 'text-blue-500' },
  feedback_status: { icon: CheckCircle2, color: 'text-green-500' },
  app_store_feedback: { icon: MessageSquare, color: 'text-purple-500' },

  // Admin
  new_user_registered: { icon: UserPlus, color: 'text-blue-500', isAdmin: true },
  secret_scan_finding: { icon: Shield, color: 'text-red-500', isAdmin: true },
  new_feedback: { icon: MessageSquare, color: 'text-purple-500', isAdmin: true },
  other_app_errors: { icon: AlertTriangle, color: 'text-red-500', isAdmin: true },
  other_app_published: { icon: CheckCircle2, color: 'text-green-500', isAdmin: true },
  public_access_requested: { icon: Globe, color: 'text-amber-500', isAdmin: true },
  admin_app_storage_quota_exceeded: { icon: HardDrive, color: 'text-red-500', isAdmin: true },
  admin_app_storage_over_quota_growth: { icon: HardDrive, color: 'text-red-500', isAdmin: true },
}

const FALLBACK: NotificationTypeConfig = { icon: Info, color: 'text-muted-foreground' }

export function getNotificationTypeConfig(type: string): NotificationTypeConfig {
  return NOTIFICATION_TYPE_CONFIG[type] || FALLBACK
}
